import PropTypes from 'prop-types';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  FormControl,
  ControlLabel,
  Row,
  Col,
  FormGroup,
  HelpBlock,
} from 'react-bootstrap';
import { countries, citiesByCountries } from 'constants/dropdownLocations';
import styles from './DualLocationDropdown.module.scss';

const cityOptionId = (city) =>
  `dual-location-city-option-${city.countryCode}-${String(city.name).replace(
    /\s+/g,
    '-'
  )}`;

const DualLocationDropdown = ({
  defaultCountry = '',
  defaultCity = '',
  onChangePlace,
  validationState = null,
  layout = 'horizontal', // 'horizontal' or 'vertical'
  // 'both' (default) | 'country' | 'city' — for chat flows that ask one field per turn
  fields = 'both',
}) => {
  const showCountryField = fields !== 'city';
  const showCityField = fields !== 'country';
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedCity, setSelectedCity] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [countryHighlightIndex, setCountryHighlightIndex] = useState(0);
  const [cityHighlightIndex, setCityHighlightIndex] = useState(0);

  const countryMenuRef = useRef(null);
  const cityMenuRef = useRef(null);

  // Initialize with default values
  useEffect(() => {
    if (defaultCountry) {
      // Find country code from country name
      const countryCode = Object.keys(countries).find(
        (code) => countries[code].toLowerCase() === defaultCountry.toLowerCase()
      );
      if (countryCode) {
        setSelectedCountry(countryCode);
        setCountrySearch(countries[countryCode]);
      } else {
        setCountrySearch(defaultCountry);
      }
    }
    if (defaultCity) {
      setSelectedCity(defaultCity);
      setCitySearch(defaultCity);
    }
  }, [defaultCountry, defaultCity, countries]);

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    if (!countrySearch) return Object.entries(countries);

    return Object.entries(countries).filter(([, name]) =>
      name.toLowerCase().includes(countrySearch.toLowerCase())
    );
  }, [countrySearch, countries]);

  // Get cities for selected country or all cities if no country selected
  const availableCities = useMemo(() => {
    if (selectedCountry && citiesByCountries[selectedCountry]) {
      return citiesByCountries[selectedCountry].map((city) => ({
        name: city,
        country: countries[selectedCountry],
        countryCode: selectedCountry,
      }));
    }

    // City-only mode requires a country — don't dump every city.
    if (fields === 'city') {
      return [];
    }

    // If no country selected, show all cities with country names
    const allCities = [];
    Object.entries(citiesByCountries).forEach(([countryCode, cities]) => {
      cities.forEach((city) => {
        allCities.push({
          name: city,
          country: countries[countryCode],
          countryCode: countryCode,
        });
      });
    });

    return allCities;
  }, [selectedCountry, citiesByCountries, countries, fields]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return availableCities;

    return availableCities.filter((city) =>
      city.name.toLowerCase().includes(citySearch.toLowerCase())
    );
  }, [citySearch, availableCities]);

  // Keep highlight in range when the filtered list changes.
  useEffect(() => {
    setCountryHighlightIndex((prev) => {
      if (!filteredCountries.length) return 0;
      return Math.min(prev, filteredCountries.length - 1);
    });
  }, [filteredCountries]);

  useEffect(() => {
    setCityHighlightIndex((prev) => {
      if (!filteredCities.length) return 0;
      return Math.min(prev, filteredCities.length - 1);
    });
  }, [filteredCities]);

  // Scroll the highlighted option into view inside the menu.
  useEffect(() => {
    if (!showCountryDropdown || !countryMenuRef.current) return;
    const item = countryMenuRef.current.querySelector(
      `[data-highlight-index="${countryHighlightIndex}"]`
    );
    if (item && typeof item.scrollIntoView === 'function') {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [countryHighlightIndex, showCountryDropdown]);

  useEffect(() => {
    if (!showCityDropdown || !cityMenuRef.current) return;
    const item = cityMenuRef.current.querySelector(
      `[data-highlight-index="${cityHighlightIndex}"]`
    );
    if (item && typeof item.scrollIntoView === 'function') {
      item.scrollIntoView({ block: 'nearest' });
    }
  }, [cityHighlightIndex, showCityDropdown]);

  // Handle country selection
  const handleCountrySelect = (countryCode, countryName) => {
    setSelectedCountry(countryCode);
    setCountrySearch(countryName);
    setShowCountryDropdown(false);
    setCountryHighlightIndex(0);

    // Country-only chat step: always clear city (asked in a later turn).
    if (fields === 'country') {
      setSelectedCity('');
      setCitySearch('');
      triggerLocationChange(countryCode, countryName, '');
      return;
    }

    // Clear city if it's not available in the new country
    if (
      selectedCity &&
      citiesByCountries[countryCode] &&
      !citiesByCountries[countryCode].includes(selectedCity)
    ) {
      setSelectedCity('');
      setCitySearch('');
      // Trigger callback with just country (no city)
      triggerLocationChange(countryCode, countryName, '');
    } else {
      // Trigger callback with country and existing city (if any)
      triggerLocationChange(countryCode, countryName, selectedCity);
    }
  };

  // Handle city selection
  const handleCitySelect = (city) => {
    setSelectedCity(city.name);
    setCitySearch(city.name);
    setShowCityDropdown(false);
    setCityHighlightIndex(0);

    // Auto-fill country if not already selected or if city is not in current country
    if (
      !selectedCountry ||
      (citiesByCountries[selectedCountry] &&
        !citiesByCountries[selectedCountry].includes(city.name))
    ) {
      setSelectedCountry(city.countryCode);
      setCountrySearch(city.country);
    }

    // Trigger callback with updated location
    triggerLocationChange(city.countryCode, city.country, city.name);
  };

  // Trigger the location change callback (empty countryCode clears parent answers)
  const triggerLocationChange = (countryCode, countryName, cityName = '') => {
    if (!onChangePlace) return;
    if (!countryCode) {
      onChangePlace({
        city: '',
        country: '',
        country_code: '',
        text: '',
        method: 'html5',
        countryOnly: 'countryOnly',
      });
      return;
    }
    onChangePlace({
      city: cityName,
      country: countryName,
      country_code: countryCode,
      text: cityName ? `${cityName}, ${countryName}` : countryName,
      method: 'html5',
      countryOnly: cityName ? 'specificAddress' : 'countryOnly',
    });
  };

  // Handle country input change
  const handleCountryInputChange = (e) => {
    const value = e.target.value;
    setCountrySearch(value);
    setShowCountryDropdown(true);
    setCountryHighlightIndex(0);

    // Cleared input: drop country + city and notify parent.
    if (!value) {
      setSelectedCountry('');
      setSelectedCity('');
      setCitySearch('');
      triggerLocationChange('', '', '');
      return;
    }

    // Editing away from a selected option invalidates it (no stale submit).
    if (
      selectedCountry &&
      countries[selectedCountry] &&
      value.trim().toLowerCase() !== countries[selectedCountry].toLowerCase()
    ) {
      setSelectedCountry('');
      setSelectedCity('');
      setCitySearch('');
      triggerLocationChange('', '', '');
    }
  };

  // Handle city input change
  const handleCityInputChange = (e) => {
    const value = e.target.value;
    setCitySearch(value);
    setShowCityDropdown(true);
    setCityHighlightIndex(0);

    // Cleared city: drop selection and notify parent so Continue disables.
    if (!value) {
      setSelectedCity('');
      if (selectedCountry) {
        triggerLocationChange(
          selectedCountry,
          countries[selectedCountry] || countrySearch,
          ''
        );
      }
    }
  };

  const moveHighlight = (current, delta, length) => {
    if (!length) return 0;
    return (current + delta + length) % length;
  };

  /** Arrow keys move highlight; Enter selects the highlighted row. */
  const handleCountryKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showCountryDropdown) {
        setShowCountryDropdown(true);
        setCountryHighlightIndex(0);
        return;
      }
      setCountryHighlightIndex((prev) =>
        moveHighlight(prev, 1, filteredCountries.length)
      );
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showCountryDropdown) {
        setShowCountryDropdown(true);
        setCountryHighlightIndex(Math.max(filteredCountries.length - 1, 0));
        return;
      }
      setCountryHighlightIndex((prev) =>
        moveHighlight(prev, -1, filteredCountries.length)
      );
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setShowCountryDropdown(false);
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!filteredCountries.length) return;
    // Closed menu: open it first so Enter alone doesn't grab the first country.
    if (!showCountryDropdown) {
      setShowCountryDropdown(true);
      setCountryHighlightIndex(0);
      return;
    }
    const index = Math.min(
      Math.max(countryHighlightIndex, 0),
      filteredCountries.length - 1
    );
    const [code, name] = filteredCountries[index];
    handleCountrySelect(code, name);
  };

  const handleCityKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!showCityDropdown) {
        setShowCityDropdown(true);
        setCityHighlightIndex(0);
        return;
      }
      setCityHighlightIndex((prev) =>
        moveHighlight(prev, 1, filteredCities.length)
      );
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (!showCityDropdown) {
        setShowCityDropdown(true);
        setCityHighlightIndex(Math.max(filteredCities.length - 1, 0));
        return;
      }
      setCityHighlightIndex((prev) =>
        moveHighlight(prev, -1, filteredCities.length)
      );
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      setShowCityDropdown(false);
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!filteredCities.length) return;
    if (!showCityDropdown) {
      setShowCityDropdown(true);
      setCityHighlightIndex(0);
      return;
    }
    const index = Math.min(
      Math.max(cityHighlightIndex, 0),
      filteredCities.length - 1
    );
    handleCitySelect(filteredCities[index]);
  };

  // Determine layout classes and structure
  const isVertical = layout === 'vertical';
  const inputColProps = isVertical ? { xs: 12 } : { sm: 8 };
  const labelClass = isVertical ? styles.verticalLabel : styles.controlLabel;

  return (
    <>
      {showCountryField ? (
        <Row className="mt++">
          <FormGroup
            controlId="DualLocationCountry"
            validationState={validationState}
          >
            {isVertical && (
              <Col xs={12}>
                <ControlLabel className={labelClass}>Country</ControlLabel>
              </Col>
            )}
            {!isVertical && (
              <Col sm={4} componentClass={ControlLabel} className={labelClass}>
                Country
              </Col>
            )}
            <Col {...inputColProps}>
              <div className={styles.dropdownWrapper}>
                <FormControl
                  type="text"
                  value={countrySearch}
                  onChange={handleCountryInputChange}
                  onKeyDown={handleCountryKeyDown}
                  onFocus={() => {
                    setShowCountryDropdown(true);
                    setCountryHighlightIndex(0);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowCountryDropdown(false);
                      // If user typed something but didn't select from dropdown, clear the input
                      if (countrySearch && !selectedCountry) {
                        setCountrySearch('');
                      }
                    }, 200);
                  }}
                  placeholder="Select your country..."
                  className={styles.dropdownInput}
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={
                    showCountryDropdown && filteredCountries.length > 0
                  }
                  aria-autocomplete="list"
                  aria-controls="dual-location-country-listbox"
                  aria-activedescendant={
                    showCountryDropdown &&
                    filteredCountries.length > 0 &&
                    countryHighlightIndex >= 0 &&
                    countryHighlightIndex < filteredCountries.length
                      ? `dual-location-country-option-${filteredCountries[countryHighlightIndex][0]}`
                      : undefined
                  }
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                />
                {showCountryDropdown && filteredCountries.length > 0 && (
                  <div
                    id="dual-location-country-listbox"
                    ref={countryMenuRef}
                    className={styles.dropdownMenu}
                    role="listbox"
                    aria-label="Countries"
                  >
                    {filteredCountries.map(([code, name], index) => {
                      const isHighlighted = index === countryHighlightIndex;
                      return (
                        <div
                          key={code}
                          id={`dual-location-country-option-${code}`}
                          role="option"
                          data-highlight-index={index}
                          aria-selected={isHighlighted}
                          className={`${styles.dropdownItem} ${
                            isHighlighted ? styles.highlighted : ''
                          } ${selectedCountry === code ? styles.selected : ''}`}
                          onMouseEnter={() => setCountryHighlightIndex(index)}
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleCountrySelect(code, name);
                          }}
                        >
                          {name}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className={styles.description}>
                Select your country for location-based job notifications
              </div>
              {validationState === 'error' && (
                <HelpBlock>
                  <span className={styles.error}>Please select a country</span>
                </HelpBlock>
              )}
            </Col>
          </FormGroup>
        </Row>
      ) : null}
      {showCityField ? (
        <Row className="mt++">
          <FormGroup controlId="DualLocationCity">
            {isVertical && (
              <Col xs={12}>
                <ControlLabel className={labelClass}>City</ControlLabel>
              </Col>
            )}
            {!isVertical && (
              <Col sm={4} componentClass={ControlLabel} className={labelClass}>
                City
              </Col>
            )}
            <Col {...inputColProps}>
              <div className={styles.dropdownWrapper}>
                <FormControl
                  type="text"
                  value={citySearch}
                  onChange={handleCityInputChange}
                  onKeyDown={handleCityKeyDown}
                  onFocus={() => {
                    setShowCityDropdown(true);
                    setCityHighlightIndex(0);
                  }}
                  onBlur={() => {
                    setTimeout(() => {
                      setShowCityDropdown(false);
                      // If user typed something but didn't select from dropdown, clear the input
                      if (citySearch && !selectedCity) {
                        setCitySearch('');
                      }
                    }, 200);
                  }}
                  placeholder="Select your nearest city..."
                  className={styles.dropdownInput}
                  autoComplete="off"
                  role="combobox"
                  aria-expanded={showCityDropdown && filteredCities.length > 0}
                  aria-autocomplete="list"
                  aria-controls="dual-location-city-listbox"
                  aria-activedescendant={
                    showCityDropdown &&
                    filteredCities.length > 0 &&
                    cityHighlightIndex >= 0 &&
                    cityHighlightIndex < filteredCities.length
                      ? cityOptionId(filteredCities[cityHighlightIndex])
                      : undefined
                  }
                  data-1p-ignore
                  data-lpignore="true"
                  data-form-type="other"
                />
                {showCityDropdown && filteredCities.length > 0 && (
                  <div
                    id="dual-location-city-listbox"
                    ref={cityMenuRef}
                    className={styles.dropdownMenu}
                    role="listbox"
                    aria-label="Cities"
                  >
                    {filteredCities.map((city, index) => {
                      const isHighlighted = index === cityHighlightIndex;
                      return (
                        <div
                          key={`${city.countryCode}-${city.name}-${index}`}
                          id={cityOptionId(city)}
                          role="option"
                          data-highlight-index={index}
                          aria-selected={isHighlighted}
                          className={`${styles.dropdownItem} ${
                            isHighlighted ? styles.highlighted : ''
                          } ${
                            selectedCity === city.name ? styles.selected : ''
                          }`}
                          onMouseEnter={() => setCityHighlightIndex(index)}
                          onMouseDown={() => handleCitySelect(city)}
                        >
                          {selectedCountry ? (
                            city.name
                          ) : (
                            <>
                              {city.name}
                              {', '}
                              <span className={styles.description}>
                                {city.country}
                              </span>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className={styles.description}>
                Receive notifications of jobs in your nearest city
              </div>
            </Col>
          </FormGroup>
        </Row>
      ) : null}
    </>
  );
};

DualLocationDropdown.propTypes = {
  defaultCountry: PropTypes.string,
  defaultCity: PropTypes.string,
  onChangePlace: PropTypes.func.isRequired,
  validationState: PropTypes.string,
  layout: PropTypes.oneOf(['horizontal', 'vertical']),
  fields: PropTypes.oneOf(['both', 'country', 'city']),
};

export default DualLocationDropdown;
